import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";

const LINKS = [
    { to: "/", label: "Accueil", testid: "nav-link-accueil" },
    { to: "/punaises-de-lit", label: "Punaises de lit", testid: "nav-link-punaises" },
    { to: "/hotels", label: "Hôtels", testid: "nav-link-hotels" },
    { to: "/tarifs", label: "Tarifs", testid: "nav-link-tarifs" },
    { to: "/zone-intervention", label: "Secteurs", testid: "nav-link-secteurs" },
    { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="main-navigation"
            className="fixed top-0 left-0 right-0 z-50 border-b border-black/10 bg-white"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
                <Link to="/" data-testid="nav-logo" className="flex items-center">
                    <img
                        src="/assets/logo.png"
                        alt="Eradikbreizh - Agréé CertiBIOCIDE"
                        className="h-11 w-auto sm:h-12"
                    />
                </Link>

                <nav className="hidden items-center gap-6 lg:flex">
                    {LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            data-testid={link.testid}
                            className={({ isActive }) =>
                                `group relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                                    isActive ? "text-night" : "text-slate-500 hover:text-night"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {link.label}
                                    <span
                                        className={`absolute -bottom-1.5 left-0 h-0.5 bg-brand transition-[width] duration-300 ${
                                            isActive ? "w-full" : "w-0 group-hover:w-full"
                                        }`}
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="tel:+33255992243"
                        data-testid="nav-phone-button"
                        className="hidden items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95 sm:inline-flex"
                    >
                        <Phone className="h-4 w-4" />
                        02 55 99 22 43
                    </a>
                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen(!open)}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/15 text-night lg:hidden"
                        aria-label="Menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden border-t border-black/10 lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-5 py-6">
                            {LINKS.map((link, i) => (
                                <motion.div
                                    key={link.to}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                >
                                    <NavLink
                                        to={link.to}
                                        data-testid={`mobile-${link.testid}`}
                                        className={({ isActive }) =>
                                            `block rounded-xl px-4 py-3 font-display text-2xl font-bold transition-colors ${
                                                isActive ? "text-brand" : "text-night"
                                            }`
                                        }
                                    >
                                        {link.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <a
                                href="tel:+33255992243"
                                data-testid="mobile-nav-phone-button"
                                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-4 font-bold text-white"
                            >
                                <Phone className="h-5 w-5" />
                                02 55 99 22 43
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
